import { AxiosResponse } from 'axios';
import { IncomingQuoteSearchResponse, IncomingQuoteSearchCriteria } from './types';
import { createApiClient } from 'services/apiClient';
import { performBulkDelete, BulkDeleteResult, BulkDeleteOptions } from 'services/bulkDelete';
import { API_BASE_URL } from 'config/api';

const apiClient = createApiClient('INCOMING_QUOTE');

export default class IncomingQuoteService {
  static async searchIncomingQuotes(tenantId: string, criteria: IncomingQuoteSearchCriteria): Promise<IncomingQuoteSearchResponse> {
    try {
      console.log('📦 [INCOMING_QUOTE] Searching incoming quotes:', {
        tenantId,
        criteria,
        url: `${API_BASE_URL}/business-documents/incoming-quotes/search/${tenantId}`,
        method: 'GET'
      });
      
      const params = new URLSearchParams();
      
      // Add query parameters
      if (criteria.document_status) params.append('document_status', criteria.document_status);
      if (criteria.created_at_start) params.append('created_at_start', criteria.created_at_start);
      if (criteria.created_at_end) params.append('created_at_end', criteria.created_at_end);
      if (criteria.document_state_id) params.append('document_state_id', criteria.document_state_id);
      if (criteria.has_been_sent != null) params.append('has_been_sent', criteria.has_been_sent.toString());
      if (criteria.parent_process_state_id) params.append('parent_process_state_id', criteria.parent_process_state_id);
      if (criteria.supplier_name) params.append('supplier_name', criteria.supplier_name);
      if (criteria.quote_number) params.append('quote_number', criteria.quote_number);
      if (criteria.max_retry_reached != null) params.append('max_retry_reached', criteria.max_retry_reached.toString());
      if (criteria.has_errors != null) params.append('has_errors', criteria.has_errors.toString());
      if (criteria.has_work_description != null) params.append('has_work_description', criteria.has_work_description.toString());
      if (criteria.days_back) params.append('days_back', criteria.days_back.toString());
      if (criteria.limit) params.append('limit', criteria.limit.toString());
      
      // Add include flags
      params.append('include_parsed_document', 'true');
      params.append('include_processing_result', 'true');
      
      const url = `/business-documents/incoming-quotes/search/${tenantId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<IncomingQuoteSearchResponse> = await apiClient.get(url);
      
      console.log('✅ [INCOMING_QUOTE] Incoming quote search response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: response.data.tenant_id,
          total_results: response.data.summary.total_results,
          result_count: response.data.results?.length || 0
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [INCOMING_QUOTE] Error searching incoming quotes:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to search incoming quotes');
    }
  }

  static async getIncomingQuoteStateDetail(tenantId: string, quoteStateId: string): Promise<any> {
    try {
      console.log('📦 [INCOMING_QUOTE] Fetching incoming quote state detail:', {
        tenantId,
        quoteStateId,
        url: `${API_BASE_URL}/business-documents/incoming-quotes/state/${tenantId}/${quoteStateId}`,
        method: 'GET'
      });
      
      const response: AxiosResponse<any> = await apiClient.get(`/business-documents/incoming-quotes/state/${tenantId}/${quoteStateId}`);
      
      console.log('✅ [INCOMING_QUOTE] Incoming quote state detail response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: tenantId,
          document_state_id: response.data.document_state_id,
          document_status: response.data.document_status,
          supplier_name: response.data.parsed_document?.supplier_name
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [INCOMING_QUOTE] Error fetching incoming quote state detail:', {
        tenantId,
        quoteStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch incoming quote state detail');
    }
  }

  static async updateIncomingQuote(tenantId: string, quoteStateId: string, incomingQuoteData: any): Promise<any> {
    try {
      console.log('📝 [INCOMING_QUOTE] Updating incoming quote:', {
        tenantId,
        quoteStateId,
        url: `${API_BASE_URL}/business-documents/incoming-quotes/state/${tenantId}/${quoteStateId}`,
        method: 'PUT'
      });
      
      const url = `/business-documents/incoming-quotes/state/${tenantId}/${quoteStateId}`;
      
      const response: AxiosResponse<any> = await apiClient.put(url, incomingQuoteData);
      
      console.log('✅ [INCOMING_QUOTE] Update incoming quote response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [INCOMING_QUOTE] Error updating incoming quote:', {
        tenantId,
        quoteStateId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to update incoming quote');
    }
  }

  static async deleteIncomingQuoteState(
    tenantId: string,
    quoteStateId: string,
    force: boolean = false
  ): Promise<void> {
    try {
      console.log('🗑️ [INCOMING_QUOTE] Deleting incoming quote state:', {
        tenantId,
        quoteStateId,
        force,
        url: `${API_BASE_URL}/business-documents/incoming-quotes/state/${tenantId}/${quoteStateId}`,
        method: 'DELETE'
      });
      
      const params = new URLSearchParams();
      if (force) {
        params.append('force', 'true');
      }
      
      const url = `/business-documents/incoming-quotes/state/${tenantId}/${quoteStateId}${params.toString() ? `?${params.toString()}` : ''}`;
      
      const response: AxiosResponse<any> = await apiClient.delete(url);
      
      console.log('✅ [INCOMING_QUOTE] Delete incoming quote state response:', {
        status: response.status,
        statusText: response.statusText
      });
    } catch (error: any) {
      console.error('❌ [INCOMING_QUOTE] Error deleting incoming quote state:', {
        tenantId,
        quoteStateId,
        force,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      
      // Re-throw with more detailed error information
      if (error.response?.data?.detail) {
        const errorMessage = new Error(error.response.data.detail);
        (errorMessage as any).status = error.response?.status;
        throw errorMessage;
      }
      throw error;
    }
  }

  static async bulkDeleteIncomingQuoteStates(
    tenantId: string,
    ids: string[],
    options?: BulkDeleteOptions,
  ): Promise<BulkDeleteResult> {
    return performBulkDelete(
      apiClient,
      'INCOMING_QUOTE',
      `/business-documents/incoming-quotes/state/${tenantId}/bulk-delete`,
      ids,
      options,
    );
  }
}

